import React, { useState, useEffect } from 'react';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import Button from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';
import Chip from '@material-ui/core/Chip';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import Divider from '@material-ui/core/Divider';
import Box from '@material-ui/core/Box';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';

import {
  Dialog,
  DialogContent,
  DialogContentText,
} from '@material-ui/core';

import TextEditor from '../Components/TextEditor';
import EmailPreview from './EmailPreview';
import MobilePreview from './MobilePreview';
import SMSPreview from './SMSPreview';
import Loader from './Loader';

import Networker from '../Utils/Networker';
import { getAllTags } from '../Utils/NotificationUtils';
import { camelToRead } from '../Utils/DataUtils';

function isEmptyRichText(value) {
  if (!value) {
    return true;
  }

  const normalized = String(value)
    .replace(/<p><br><\/p>/gi, '')
    .replace(/<p><\/p>/gi, '')
    .replace(/<br\s*\/?>/gi, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .trim();

  return normalized.length === 0;
}

function richTextContainsImage(value) {
  return /<img\b[^>]*>/i.test(String(value || ''));
}

const matchTags = (message, tags) => {
  const getTagRx = (tag) => new RegExp('[[](' + tag + ')[\\]]', 'gm');

  return tags
    .map((tag) => message.match(getTagRx(tag)))
    .map((matched, i) => [tags[i], !!matched]);
};

const createDefaultBulkNotification = (productId) => ({
  message: '',
  subject: '',
  trigger: {
    type: 'manual',
    field: '',
    value: '',
    value1: '',
    field1: '',
  },
  active: false,
  notificationType: 'push',
  productIds: [productId],
  associatedLink: null,
  onOccasion: false,
  superuserOnly: true,
  userInfo: {},
});

const sanitizePresetForBulkSend = (notification, productId) => {
  const next = {
    ...notification,
    productIds: [productId],
    active: false,
    superuserOnly: true,
    trigger: {
      type: 'manual',
      field: '',
      value: '',
      value1: '',
      field1: '',
    },
  };

  delete next._id;
  delete next.__v;
  delete next.createdAt;
  delete next.updatedAt;

  return next;
};

export default function BulkNotificationEditor({
  selectedRows,
  currentProductId,
  productData,
  onSendNotification,
  onClose,
}) {
  const [allTags, setAllTags] = useState([]);
  const [openMobilePreview, setOpenMobilePreview] = useState(false);
  const [openEmailPreview, setOpenEmailPreview] = useState(false);
  const [openSMSPreview, setOpenSMSPreview] = useState(false);
  const [showError, setShowError] = useState(false);
  const [loading, setLoading] = useState(false);

  const [selectedPresetId, setSelectedPresetId] = useState('');
  const [showRecipients, setShowRecipients] = useState(false);
  const [manualPresets, setManualPresets] = useState([]);
  const [loadingPresets, setLoadingPresets] = useState(false);

  const [newNotification, setNewNotification] = useState(
    createDefaultBulkNotification(currentProductId)
  );

  const [tagMatches, setTagMatches] = useState([]);

  const [quillValue, setQuillValue] = useState({
    editorHtml: '',
    title: '',
    toggle: false,
    error: null,
  });

  const [sendResults, setSendResults] = useState(null);

  useEffect(() => {
    getAllTags().then(setAllTags).catch(console.error);
  }, []);

  useEffect(() => {
    const message =
      newNotification.notificationType === 'text'
        ? newNotification.message
        : quillValue.editorHtml;

    setTagMatches(matchTags(message || '', allTags));
  }, [
    allTags,
    newNotification.message,
    newNotification.notificationType,
    quillValue.editorHtml,
  ]);

  const availablePresets = React.useMemo(() => {
    const byId = {};

    manualPresets.forEach((notification) => {
      if (notification?._id) {
        byId[notification._id] = notification;
      }
    });

    return Object.values(byId);
  }, [manualPresets]);

  const selectedPreset = availablePresets.find((preset) => preset._id === selectedPresetId);

  const selectedUserIds = selectedRows
    .map((row) => row.userId)
    .filter(Boolean);

  const uniqueSelectedUserIds = Array.from(new Set(selectedUserIds.map(String)));

  const countRows = (predicate) => selectedRows.filter(predicate).length;

  function isBlank(value) {
    return value === null || value === undefined || String(value).trim() === '';
}

  const recipientSummary = {
    selected: selectedRows.length,
    uniqueUsers: uniqueSelectedUserIds.length,

    pushDisabled: countRows((row) => row.derived?.pushEnabled === false),
    smsDisabled: countRows((row) => row.derived?.smsEnabled === false),
    emailDisabled: countRows((row) => row.derived?.emailEnabled === false),

    invalidEmail: countRows((row) => row.derived?.validEmail === false),
    invalidPhone: countRows((row) => row.derived?.validPhone === false),

    missingDeviceVendorIdentifier: countRows((row) => row.derived?.missingDeviceVendorIdentifier === true),
    missingFirebaseToken: countRows((row) => isBlank(row.firebaseToken)),
  };

  const applyNotificationToEditor = (notification) => {
    setNewNotification(notification);

    setQuillValue((prev) => ({
      ...prev,
      editorHtml: notification.message || '',
      title: notification.subject || '',
      toggle: !prev.toggle,
      error: null,
    }));
  };

  useEffect(() => {
    const refreshManualPresets = async () => {
        setLoadingPresets(true);

        try {
        const res = await Networker.get({
            root: 'notifications',
            inner: 'all',
            query: {
            'trigger.type': 'manual',
            },
        });

        setManualPresets(Array.isArray(res.body) ? res.body : []);
        } catch (err) {
        console.error('Failed to fetch manual notification presets:', err);
        setManualPresets([]);
        } finally {
        setLoadingPresets(false);
        }
    };

    refreshManualPresets();
    }, []);

  const handleApplyContentOnly = () => {
    if (!selectedPreset) {
      return;
    }

    const next = {
      ...newNotification,
      subject: selectedPreset.subject || '',
      message: selectedPreset.message || '',
      notificationType: selectedPreset.notificationType || newNotification.notificationType,
    };

    applyNotificationToEditor(next);
  };

  const handleApplyFullPreset = () => {
    if (!selectedPreset) {
      return;
    }

    applyNotificationToEditor(
      sanitizePresetForBulkSend(selectedPreset, currentProductId)
    );
  };

  const handleChange = (name) => (event) => {
    const value = event.target.value;

    console.info('handleChange:', {
        name,
        value,
        currentNotificationType: newNotification.notificationType,
    });

    if (name.startsWith('push.')) {
      const key = name.split('.')[1];

      if (key === 'action' && (value === '' || value == null)) {
        const nextUserInfo = { ...(newNotification.userInfo || {}) };
        delete nextUserInfo.action;

        setNewNotification({
          ...newNotification,
          userInfo: nextUserInfo,
        });

        return;
      }

      setNewNotification({
        ...newNotification,
        userInfo: {
          ...(newNotification.userInfo || {}),
          [key]: value,
        },
      });

      return;
    }

    if (name === 'survey') {
      setNewNotification({
        ...newNotification,
        associatedLink: value,
      });

      return;
    }

    if (event.target.hasOwnProperty('checked')) {
      setNewNotification({
        ...newNotification,
        [name]: event.target.checked,
      });

      return;
    }

    setNewNotification({
      ...newNotification,
      [name]: value,
    });
  };

  const buildNotificationPayload = () => {
    const hasTitle = ['push', 'email'].includes(newNotification.notificationType);

    if (hasTitle) {
      return {
        ...newNotification,
        message: quillValue.editorHtml,
        subject: quillValue.title,
        actionButton: false,
        active: false,
        superuserOnly: true,
        trigger: {
          type: 'manual',
          field: '',
          value: '',
          value1: '',
          field1: '',
        },
      };
    }

    return {
      ...newNotification,
      active: false,
      superuserOnly: true,
      trigger: {
        type: 'manual',
        field: '',
        value: '',
        value1: '',
        field1: '',
      },
    };
  };

  const validateBeforeSend = () => {
    if (!uniqueSelectedUserIds.length) {
      alert('No valid users selected.');
      return false;
    }

    if (['push', 'email'].includes(newNotification.notificationType)) {
      if (quillValue.error) {
        setShowError(true);
        return false;
      }

      if (!quillValue?.title) {
        alert('No title/subject!');
        return false;
      }

      if (isEmptyRichText(quillValue?.editorHtml)) {
        alert('No message!');
        return false;
      }
    }

    if (
      newNotification.notificationType === 'push' &&
      richTextContainsImage(quillValue?.editorHtml)
    ) {
      alert('Push notifications cannot include embedded images in the message body. Use the "Show Image" field instead.');
      return false;
    }

    if (newNotification.notificationType === 'text' && !newNotification.message) {
      alert('No message!');
      return false;
    }

    return true;
  };

  const handleSend = async () => {
    if (!validateBeforeSend()) {
      return;
    }

    setLoading(true);
    setSendResults(null);

    try {
      const payload = {
        productId: currentProductId,
        userIds: uniqueSelectedUserIds,
        notification: buildNotificationPayload(),
      };

      console.info('Manual notification payload:', JSON.stringify(payload, null, 2));
      const result = await onSendNotification(payload);
      setSendResults(result);
    } catch (err) {
      console.error(err);

      setSendResults({
        ok: false,
        error: err.message || 'Failed to send bulk notification.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (loading) {
      return;
    }

    onClose();
  };

  const renderMobilePreview = () => {
    if (!openMobilePreview) {
      return null;
    }

    return (
      <MobilePreview
        actionButton={[]}
        quillValue={quillValue}
        setOpenMobilePreview={setOpenMobilePreview}
        openMobilePreview={openMobilePreview}
      />
    );
  };

  const renderEmailPreview = () => {
    if (!openEmailPreview) {
        return null;
    }

    return (
        <EmailPreview
            quillValue={quillValue}
            openEmailPreview={openEmailPreview}
            setOpenEmailPreview={setOpenEmailPreview}
            to={`${uniqueSelectedUserIds.length} selected users`}
        />
    );
  };

  const renderSMSPreview = () => {
    if (!openSMSPreview) {
        return null;
    }

    return (
        <SMSPreview
        message={newNotification.message}
        openSMSPreview={openSMSPreview}
        setOpenSMSPreview={setOpenSMSPreview}
        to={`${uniqueSelectedUserIds.length} selected users`}
        />
    );
    };

  const renderErrorDialog = () => (
    <Dialog
      open={showError}
      onClose={() => setShowError(false)}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogContent>
        <DialogContentText
          id="alert-dialog-description"
          style={{ textAlign: 'center', fontSize: '1.3rem', padding: '3rem' }}
        >
          {quillValue.error}
        </DialogContentText>
      </DialogContent>
    </Dialog>
  );

  const renderTextField = () => {
    console.info('renderTextField notificationType:', newNotification.notificationType);
    switch (newNotification.notificationType) {
      case 'text':
        return (
          <>
            <br />
            <TextField
              multiline
              rows="6"
              fullWidth
              rowsMax="32"
              label="Message"
              size="small"
              variant="outlined"
              value={newNotification.message}
              onChange={handleChange('message')}
            />

            <Button
                style={{ marginTop: 8 }}
                variant="outlined"
                color="primary"
                onClick={() => setOpenSMSPreview(true)}
            >
                Preview SMS Message
            </Button>
          </>
        );

      case 'email':
      case 'push':
        return (
          <>
            <TextField
              fullWidth
              label="Subject"
              value={quillValue.title}
              style={{ marginBottom: 15, marginTop: 15 }}
              variant="outlined"
              required
              onChange={(e) => {
                e.persist();
                setQuillValue((prev) => ({
                  ...prev,
                  title: e.target?.value,
                }));
              }}
            />

            <TextEditor
                key={`editor-${newNotification.notificationType}-${quillValue.toggle}`}
                setOpenPreview={() => {
                    if (newNotification.notificationType === 'email') {
                    setOpenEmailPreview(true);
                    } else {
                    setOpenMobilePreview(true);
                    }
                }}
                setQuillValue={setQuillValue}
                quillValue={quillValue}
                setLoading={setLoading}
            />
          </>
        );

      default:
        return null;
    }
  };

  const renderTags = () =>
    tagMatches.map(([key, match]) => (
      <Tooltip style={{ marginRight: 6, marginTop: 10 }} title={`[${key}]`} key={key}>
        <Chip
          variant="outlined"
          color={match ? 'secondary' : 'primary'}
          size="small"
          onClick={() => {
            if (['email', 'push'].includes(newNotification.notificationType)) {
              setQuillValue((prev) => ({
                editorHtml: prev.editorHtml,
                ...prev,
                toggle: !prev.toggle,
                tag: `[${key}]`,
              }));
            } else {
              setNewNotification({
                ...newNotification,
                message: `${newNotification.message || ''}[${key}]`,
              });
            }
          }}
          label={camelToRead(key)}
        />
      </Tooltip>
    ));

  const renderPushInfo = () => {
    if (newNotification.notificationType !== 'push') {
      return null;
    }

    if (productData?.pushFields?.length) {
      const triples = productData.pushFields.reduce((acc, _, idx, arr) => {
        if (!(idx % 3)) {
          acc.push(arr.slice(idx, idx + 3));
        }

        return acc;
      }, []);

      return (
        <>
          {triples.map((arr, groupIndex) => {
            const len = 12 / arr.length;

            return arr.map((ent) => (
              <Grid item xs={len} key={`${groupIndex}-${ent}`}>
                <TextField
                  fullWidth
                  label={camelToRead(ent)}
                  value={newNotification?.userInfo?.[ent] ?? ''}
                  onChange={handleChange(`push.${ent}`)}
                />
              </Grid>
            ));
          })}
        </>
      );
    }

    return (
      <>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Open Link"
            value={newNotification?.userInfo?.link ?? ''}
            onChange={handleChange('push.link')}
          />
        </Grid>

        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Show Image"
            value={newNotification?.userInfo?.image ?? ''}
            onChange={handleChange('push.image')}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            select
            label="Action"
            value={newNotification?.userInfo?.action ?? ''}
            onChange={handleChange('push.action')}
          >
            <MenuItem value="">None</MenuItem>
            <MenuItem value="openCamera">openCamera</MenuItem>
            <MenuItem value="takePhoto">takePhoto</MenuItem>
            <MenuItem value="recordVideo">recordVideo</MenuItem>
            <MenuItem value="uploadFile">uploadFile</MenuItem>
          </TextField>
        </Grid>
      </>
    );
  };

  const getPresetLabel = (notification) => {
      const subject = String(notification.subject || '').trim();
      const type = String(notification.notificationType || '').toUpperCase();
      const messageText = String(notification.message || '')
          .replace(/<[^>]+>/g, '')
          .replace(/&nbsp;/g, ' ')
          .trim();

      if (subject) {
          return `${type} - ${subject}`;
      }

      if (messageText) {
          return `${type} - ${messageText.slice(0, 80)}`;
      }

      return `${type} - ${notification._id}`;
  };

  const getPresetPreviewText = (notification) => {
      const subject = String(notification.subject || '').trim();
      const messageText = String(notification.message || '')
          .replace(/<[^>]+>/g, '')
          .replace(/&nbsp;/g, ' ')
          .trim();

      return [
          subject ? `Subject: ${subject}` : null,
          messageText ? `Message: ${messageText.slice(0, 250)}` : null,
      ].filter(Boolean).join('\n');
  };

  const renderPresetSection = () => (
      <Paper style={{ padding: 12, marginBottom: 16 }} variant="outlined">
          <Typography variant="subtitle1" style={{ marginBottom: 8 }}>
          Manual Notification Preset
          </Typography>

          <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} md={6}>
              <TextField
              fullWidth
              select
              label="Past Manual Notification"
              value={selectedPresetId}
              onChange={(e) => setSelectedPresetId(e.target.value)}
              variant="outlined"
              size="small"
              disabled={loadingPresets}
              >
              <MenuItem value="">
                  {loadingPresets ? 'Loading presets...' : 'None'}
              </MenuItem>

              {availablePresets
                  .filter((notification) => notification?.trigger?.type === 'manual')
                  .map((notification) => (
                  <MenuItem key={notification._id} value={notification._id}>
                      <Tooltip
                      title={
                          <span style={{ whiteSpace: 'pre-line' }}>
                          {getPresetPreviewText(notification)}
                          </span>
                      }
                      placement="right"
                      >
                      <span>{getPresetLabel(notification)}</span>
                      </Tooltip>
                  </MenuItem>
                  ))}
              </TextField>
          </Grid>

          <Grid item xs={12} md={3}>
              <Button
              fullWidth
              variant="outlined"
              color="primary"
              disabled={!selectedPreset}
              onClick={handleApplyContentOnly}
              >
              Apply Content
              </Button>
          </Grid>

          <Grid item xs={12} md={3}>
              <Button
              fullWidth
              variant="outlined"
              color="secondary"
              disabled={!selectedPreset}
              onClick={handleApplyFullPreset}
              >
              Apply Full Preset
              </Button>
          </Grid>

          {selectedPreset && (
              <Grid item xs={12}>
              <Paper style={{ padding: 12, backgroundColor: '#fafafa' }} variant="outlined">
                  <Typography variant="caption" color="textSecondary">
                  Selected Preset Preview
                  </Typography>

                  {!!selectedPreset.subject && (
                  <Typography variant="body2" style={{ marginTop: 4 }}>
                      <strong>Subject:</strong> {selectedPreset.subject}
                  </Typography>
                  )}

                  <Typography variant="body2" style={{ marginTop: 4 }}>
                  <strong>Type:</strong> {selectedPreset.notificationType}
                  </Typography>

                  <div
                  style={{
                      marginTop: 8,
                      maxHeight: 120,
                      overflowY: 'auto',
                      padding: 8,
                      border: '1px solid #ddd',
                      backgroundColor: '#fff',
                  }}
                  dangerouslySetInnerHTML={{
                      __html: selectedPreset.message || '',
                  }}
                  />
              </Paper>
              </Grid>
          )}
          </Grid>
      </Paper>
  );

  const renderRecipientSummary = () => (
    <Paper style={{ padding: 12, marginBottom: 16 }} variant="outlined">
      <Grid container spacing={1} alignItems="center">
        <Grid item xs={12} md={8}>
          <Typography variant="subtitle1">
            Recipients
          </Typography>

          <Typography variant="body2" color="textSecondary">
            Selected rows: {recipientSummary.selected} | Unique users: {recipientSummary.uniqueUsers}
          </Typography>

          <Typography variant="body2" color="textSecondary">
            Push disabled: {recipientSummary.pushDisabled} | SMS disabled: {recipientSummary.smsDisabled} | Email disabled: {recipientSummary.emailDisabled}
          </Typography>

          <Typography variant="body2" color="textSecondary">
            Invalid email: {recipientSummary.invalidEmail} | Invalid phone: {recipientSummary.invalidPhone}
          </Typography>

          <Typography variant="body2" color="textSecondary">
            Missing DVI: {recipientSummary.missingDeviceVendorIdentifier} | Missing Firebase token: {recipientSummary.missingFirebaseToken}
          </Typography>
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControlLabel
            control={
              <Switch
                checked={showRecipients}
                onChange={(e) => setShowRecipients(e.target.checked)}
                color="primary"
              />
            }
            label="Show Selected Recipients"
          />
        </Grid>

        {showRecipients && (
          <Grid item xs={12}>
            <Divider style={{ margin: '8px 0' }} />

            <Box style={{ maxHeight: 160, overflowY: 'auto' }}>
              {selectedRows.map((row) => (
                <Typography variant="caption" display="block" key={`${row.id}-${row.userId}`}>
                  {row.email || 'No email'} | {row.userId || 'No userId'} | {row.labels || 'No Macs'}
                </Typography>
              ))}
            </Box>
          </Grid>
        )}
      </Grid>
    </Paper>
  );

  const renderSendResults = () => {
    if (!sendResults) {
      return null;
    }

    const finalCompleted = sendResults?.final?.completed?.length || 0;
    const finalFailed = sendResults?.final?.failed?.length || 0;
    const finalWaiting = sendResults?.final?.waiting?.length || 0;

    return (
      <Paper style={{ padding: 12, marginTop: 16 }} variant="outlined">
        <Typography variant="subtitle1">
          Send Results
        </Typography>

        {!sendResults.ok && sendResults.error && (
          <Typography color="error" variant="body2">
            {sendResults.error}
          </Typography>
        )}

        <Typography variant="body2">
          Attempted: {sendResults.attempted || 0}
        </Typography>

        <Typography variant="body2">
          Queued: {sendResults.queued?.length || 0}
        </Typography>

        <Typography variant="body2">
          Skipped: {sendResults.skipped?.length || 0}
        </Typography>

        <Typography variant="body2">
          Failed Before Send: {sendResults.failed?.length || 0}
        </Typography>

        {sendResults.final && (
          <Typography variant="body2">
            Completed: {finalCompleted} | Failed: {finalFailed} | Waiting: {finalWaiting}
          </Typography>
        )}

        {!!sendResults.skipped?.length && (
          <>
            <Divider style={{ margin: '8px 0' }} />
            <Typography variant="subtitle2">Skipped</Typography>
            {sendResults.skipped.map((entry, index) => (
              <Typography variant="caption" display="block" key={`skipped-${index}`}>
                {entry.email || entry.userId}: {entry.reason}
              </Typography>
            ))}
          </>
        )}

        {!!sendResults.failed?.length && (
          <>
            <Divider style={{ margin: '8px 0' }} />
            <Typography variant="subtitle2">Failed</Typography>
            {sendResults.failed.map((entry, index) => (
              <Typography variant="caption" display="block" key={`failed-${index}`}>
                {entry.email || entry.userId}: {entry.reason}
              </Typography>
            ))}
          </>
        )}
      </Paper>
    );
  };

  return (
    <div>
      {renderMobilePreview()}
      {renderEmailPreview()}
      {renderSMSPreview()}
      <Loader show={loading} />
      {renderErrorDialog()}

      {renderRecipientSummary()}
      {renderPresetSection()}

      {renderTextField()}

      <Grid container spacing={1} direction="row" justifyContent="flex-start">
        <Grid item xs={12}>
          {renderTags()}
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            select
            label="Type"
            value={newNotification.notificationType}
            onChange={handleChange('notificationType')}
          >
            <MenuItem value="email">Email</MenuItem>
            <MenuItem value="text">Text</MenuItem>
            <MenuItem value="push">Push Notification</MenuItem>
          </TextField>
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Trigger"
            value="Manual"
            disabled
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Selected Users"
            value={uniqueSelectedUserIds.length}
            disabled
          />
        </Grid>

        {renderPushInfo()}

        <Grid item xs={6}>
          <Button
            style={{ marginTop: 8, marginBottom: 4 }}
            fullWidth
            color="secondary"
            variant="contained"
            onClick={handleSend}
            disabled={loading || !uniqueSelectedUserIds.length}
          >
            Send
          </Button>
        </Grid>

        <Grid item xs={6}>
          <Button
            style={{ marginTop: 8, marginBottom: 4 }}
            fullWidth
            color="primary"
            onClick={handleCancel}
            disabled={loading}
          >
            Cancel
          </Button>
        </Grid>
      </Grid>

      {renderSendResults()}
    </div>
  );
}